import { useState } from "react";
import { Clock, Trash2, Compass, GitBranch, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const confColor = (label) => {
  if (label === "High") return "text-[#39FF14]";
  if (label === "Medium") return "text-[#FFB800]";
  return "text-[#FF6666]";
};

const timeAgo = (iso) => {
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function HistoryList({ items, onDelete, onClear, onSelect }) {
  const [open, setOpen] = useState(true);

  if (!items || items.length === 0) return null;

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-5 sm:p-6 shadow-2xl" data-testid="history-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left mb-1"
        data-testid="history-toggle"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-[#0F0F13] border border-white/10 flex items-center justify-center">
            <Clock className="w-4 h-4 text-[#FF2D55]" strokeWidth={2} />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-display font-bold text-white text-sm tracking-tight">
              Recent analyses
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">
              {items.length} song{items.length === 1 ? "" : "s"} · stored locally on server
            </span>
          </div>
        </div>
        {open ? (
          <ChevronUp className="w-4 h-4 text-zinc-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-500" />
        )}
      </button>

      {open && (
        <>
          <ul className="mt-4 flex flex-col gap-2" data-testid="history-list">
            {items.map((item) => (
              <li
                key={item.id}
                data-testid={`history-item-${item.id}`}
                className="group bg-[#0F0F13] hover:bg-[#14141A] border border-white/5 hover:border-[#FF2D55]/30 rounded-2xl p-3.5 flex items-center gap-3 transition-all cursor-pointer"
                onClick={() => onSelect(item)}
              >
                <div className="w-12 h-12 rounded-xl bg-[#0A0A0A] border border-white/10 flex flex-col items-center justify-center shrink-0">
                  <span className="font-mono text-[10px] text-[#39FF14] font-bold leading-none">
                    {item.alternate_key_notation}
                  </span>
                  <span className="font-display text-[11px] text-zinc-500 mt-0.5 leading-none">
                    Camelot
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-semibold text-white truncate font-body"
                    title={item.song_name}
                    data-testid={`history-song-${item.id}`}
                  >
                    {item.song_name}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-zinc-500 font-mono">
                    <span className="text-[#FF2D55] font-semibold" data-testid={`history-key-${item.id}`}>
                      {item.detected_key}
                    </span>
                    {item.bpm != null && (
                      <>
                        <span className="text-zinc-700">·</span>
                        <span className="text-white font-semibold" data-testid={`history-bpm-${item.id}`}>
                          {item.bpm} BPM
                        </span>
                      </>
                    )}
                    <span className="text-zinc-700">·</span>
                    <span className={confColor(item.confidence)}>{item.confidence}</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-[10px] text-zinc-600 font-mono">{timeAgo(item.created_at)}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item.id);
                    }}
                    data-testid={`history-delete-${item.id}`}
                    aria-label="Delete history item"
                    className="w-7 h-7 rounded-full bg-transparent hover:bg-red-500/10 flex items-center justify-center text-zinc-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center justify-between">
            <span className="text-[10px] text-zinc-600 font-body">
              Audio files are never stored — only key results.
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="history-clear-button"
                  className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 hover:text-red-400 font-mono font-semibold transition-colors"
                >
                  Clear all
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#0A0A0A] border border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-display flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#FFB800]" />
                    Clear all history?
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400 font-body">
                    This permanently deletes all {items.length} saved analyses. You won&apos;t be able to recover them. Audio files were never stored, so this only removes the key metadata.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    data-testid="history-clear-cancel"
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="history-clear-confirm"
                    onClick={onClear}
                    className="bg-red-500 text-white hover:bg-red-600"
                  >
                    Yes, clear all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      )}
    </div>
  );
}
